import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Tabs,
  Tab,
  Menu,
  useTheme,
  Container,
  InputAdornment,
  Switch,
  FormControlLabel,
  LinearProgress,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  AdminPanelSettings as AdminIcon,
  Shield as ShieldIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Storage as StorageIcon,
  TrendingUp as TrendingUpIcon,
  IntegrationInstructions as IntegrationIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Google as GoogleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAccount } from '../contexts/AccountContext';
import { format } from 'date-fns';
import { getAccountIntegrations, connectGmailIntegration, disconnectIntegration, cleanupInvalidIntegrations, checkGmailConflict, AccountIntegration, GmailConflictCheck } from '../api/handleIntegrations';
import { supabase } from '../supabaseClient';
import { API_BASE_URL } from '../config/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const AccountManager = () => {
  const theme = useTheme();
  const {
    currentAccount,
    accountDetails,
    accountUsers,
    loading,
    error,
    refreshAccountDetails,
    refreshAccountUsers,
    updateAccountSettings,
    inviteUserToAccount,
    updateUserRole,
    removeUserFromAccount,
    canManageUsers,
    canManageSettings,
    canManageIntegrations,
    isImpersonating,
    impersonatedUser,
    originalAdminUser,
  } = useAccount();

  const [activeTab, setActiveTab] = useState(0);
  const [success, setSuccess] = useState<string>('');
  const [localError, setLocalError] = useState<string>('');

  // Search and filters
  const [userSearch, setUserSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  // Dialog states
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [gmailDialog, setGmailDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  // Form states
  const [settingsForm, setSettingsForm] = useState({
    name: '',
    plan_type: '',
  });

  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'member' as 'owner' | 'admin' | 'member',
  });

  const [editUserForm, setEditUserForm] = useState({
    role: 'member' as 'owner' | 'admin' | 'member',
  });

  const [gmailForm, setGmailForm] = useState({
    emailAddress: '',
  });

  const [gmailConflictData, setGmailConflictData] = useState<GmailConflictCheck | null>(null);
  const [checkingConflict, setCheckingConflict] = useState(false);

  const [saving, setSaving] = useState(false);

  // Integration states
  const [integrations, setIntegrations] = useState<AccountIntegration[]>([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [integrationsError, setIntegrationsError] = useState<string>('');

  useEffect(() => {
    if (currentAccount) {
      refreshAccountDetails();
      refreshAccountUsers();
      loadIntegrations();
    }
  }, [currentAccount]);

  // Check for OAuth success callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const connected = urlParams.get('connected');

    if (connected === 'gmail') {
      setSuccess('Gmail integration connected successfully!');

      // Clean up URL parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('connected');
      window.history.replaceState(null, '', newUrl.toString());

      // Refresh integrations to show the new one
      if (currentAccount) {
        setTimeout(() => loadIntegrations(), 1000);
      }
    }
  }, [currentAccount]);

  useEffect(() => {
    if (accountDetails) {
      setSettingsForm({
        name: accountDetails.name,
        plan_type: accountDetails.plan_type,
      });
    }
  }, [accountDetails]);

  const handleSaveSettings = async () => {
    if (!currentAccount) return;

    try {
      setSaving(true);
      setLocalError('');

      await updateAccountSettings(currentAccount.account_id, {
        name: settingsForm.name,
        plan_type: settingsForm.plan_type,
      });

      setSuccess('Account settings updated successfully');
      setSettingsDialog(false);
    } catch (err: any) {
      setLocalError(err.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInviteUser = async () => {
    if (!currentAccount) return;

    try {
      setSaving(true);
      setLocalError('');

      await inviteUserToAccount(currentAccount.account_id, inviteForm.email, inviteForm.role);

      setSuccess(`User invited successfully as ${inviteForm.role}`);
      setInviteDialog(false);
      setInviteForm({ email: '', role: 'member' });
    } catch (err: any) {
      setLocalError(err.message || 'Failed to invite user');
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = (user: any) => {
    setSelectedUser(user);
    setEditUserForm({ role: user.role });
    setEditUserDialog(true);
  };

  const handleSaveUserEdit = async () => {
    if (!currentAccount || !selectedUser) return;

    try {
      setSaving(true);
      setLocalError('');

      await updateUserRole(currentAccount.account_id, selectedUser.user_id, editUserForm.role);

      setSuccess('User role updated successfully');
      setEditUserDialog(false);
    } catch (err: any) {
      setLocalError(err.message || 'Failed to update user role');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!currentAccount) return;

    try {
      setSaving(true);
      setLocalError('');

      await removeUserFromAccount(currentAccount.account_id, userId);

      setSuccess('User removed from account successfully');
    } catch (err: any) {
      setLocalError(err.message || 'Failed to remove user');
    } finally {
      setSaving(false);
    }
  };

  const loadIntegrations = async () => {
    if (!currentAccount) return;

    try {
      setIntegrationsLoading(true);
      setIntegrationsError('');

      let fetchedIntegrations = null;
      let error = null;

      if (isImpersonating && impersonatedUser) {
        // Use impersonation endpoint for integrations
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        const token = (await supabase.auth.getSession()).data.session?.access_token;

        const response = await fetch(`${API_BASE_URL}/admin/impersonate/${impersonatedUser.id}/account/${currentAccount.account_id}/integrations?currentUserId=${currentUser?.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch impersonated integrations');
        }

        const responseData = await response.json();
        fetchedIntegrations = responseData.integrations;
      } else {
        // Normal integrations fetching
        const result = await getAccountIntegrations(currentAccount.account_id);
        fetchedIntegrations = result.integrations;
        error = result.error;
      }

      if (error) {
        setIntegrationsError(error);
      } else {
        setIntegrations(fetchedIntegrations || []);
      }
    } catch (err: any) {
      setIntegrationsError(err.message || 'Failed to load integrations');
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const handleConnectGmail = async () => {
    if (!currentAccount) return;

    // Show Gmail connection dialog first
    setGmailForm({ emailAddress: '' });
    setGmailConflictData(null);
    setGmailDialog(true);
  };

  const handleCheckGmailConflict = async () => {
    if (!currentAccount || !gmailForm.emailAddress) return;

    try {
      setCheckingConflict(true);
      setLocalError('');

      const impersonationData = isImpersonating && impersonatedUser ? {
        isImpersonating: true,
        impersonatedUserId: impersonatedUser.id,
      } : undefined;

      const { data, error } = await checkGmailConflict(currentAccount.account_id, gmailForm.emailAddress, impersonationData);

      if (error) {
        setLocalError(error);
        setGmailConflictData(null);
      } else {
        setGmailConflictData(data);
      }
    } catch (err: any) {
      setLocalError(err.message || 'Failed to check Gmail conflict');
      setGmailConflictData(null);
    } finally {
      setCheckingConflict(false);
    }
  };

  const handleProceedWithGmail = async () => {
    if (!currentAccount) return;

    try {
      setLocalError('');

      const impersonationData = isImpersonating && impersonatedUser ? {
        isImpersonating: true,
        impersonatedUserId: impersonatedUser.id,
      } : undefined;

      const { success, error } = await connectGmailIntegration(currentAccount.account_id, impersonationData);

      if (error) {
        setLocalError(error);
      } else {
        setGmailDialog(false);
        // If successful, the page will redirect to Gmail OAuth
      }
    } catch (err: any) {
      setLocalError(err.message || 'Failed to connect Gmail');
    }
  };

  const handleDisconnectIntegration = async (integrationId: string, emailAddress: string) => {
    if (!currentAccount) return;

    const confirmDisconnect = window.confirm(
      `Are you sure you want to disconnect ${emailAddress}? This will stop automatic email processing for this account.`
    );

    if (!confirmDisconnect) return;

    try {
      setSaving(true);
      setLocalError('');

      const impersonationData = isImpersonating && impersonatedUser ? {
        isImpersonating: true,
        impersonatedUserId: impersonatedUser.id,
      } : undefined;

      const { success, error } = await disconnectIntegration(currentAccount.account_id, integrationId, impersonationData);

      if (error) {
        setLocalError(error);
      } else {
        setSuccess('Integration disconnected successfully');
        loadIntegrations(); // Refresh the list
      }
    } catch (err: any) {
      setLocalError(err.message || 'Failed to disconnect integration');
    } finally {
      setSaving(false);
    }
  };

  const handleCleanupIntegrations = async () => {
    if (!currentAccount) return;

    const confirmCleanup = window.confirm(
      'This will remove all invalid email integrations from this account. You can then reconnect them if needed. Continue?'
    );

    if (!confirmCleanup) return;

    try {
      setSaving(true);
      setLocalError('');

      const impersonationData = isImpersonating && impersonatedUser ? {
        isImpersonating: true,
        impersonatedUserId: impersonatedUser.id,
      } : undefined;

      const { success, error } = await cleanupInvalidIntegrations(currentAccount.account_id, impersonationData);

      if (error) {
        setLocalError(error);
      } else {
        setSuccess('Invalid integrations cleaned up successfully. You can now reconnect your Gmail account.');
        loadIntegrations(); // Refresh the list
      }
    } catch (err: any) {
      setLocalError(err.message || 'Failed to cleanup integrations');
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = accountUsers.filter(user => {
    const matchesSearch = userSearch === '' ||
      user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(userSearch.toLowerCase());

    const matchesRole = roleFilter === 'all' || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return 'N/A';
    }

    try {
      const date = new Date(dateString);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return format(date, 'MMM dd, yyyy');
    } catch (error) {
      console.warn('Error formatting date:', dateString, error);
      return 'Invalid Date';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'error';
      case 'admin': return 'primary';
      case 'member': return 'default';
      default: return 'default';
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'enterprise': return 'error';
      case 'pro': return 'primary';
      case 'essentials': return 'secondary';
      case 'starter': return 'default';
      default: return 'default';
    }
  };

  const getUsagePercentage = (current: number, limit: number) => {
    if (limit === -1) return 0; // Unlimited
    if (limit === 0) return 100;
    return Math.min((current / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'primary';
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={40} />
        </Box>
      </Container>
    );
  }

  if (!currentAccount) {
    return (
      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <Alert severity="warning">
          <Typography variant="h6" sx={{ mb: 1 }}>No Account Selected</Typography>
          Please select an account to manage.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Account Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage settings, users, and integrations for {currentAccount.account_name}
        </Typography>
      </Box>

      {/* Alerts */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {(error || localError) && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setLocalError('')}>
          {error || localError}
        </Alert>
      )}

      {/* Account Overview Cards */}
      {accountDetails && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Plan
                    </Typography>
                    <Chip
                      label={accountDetails.plan_type}
                      color={getPlanColor(accountDetails.plan_type) as any}
                      sx={{ textTransform: 'capitalize', mt: 1 }}
                    />
                  </Box>
                  <BusinessIcon sx={{ fontSize: 40, color: theme.palette.primary.main, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Users
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                      {accountUsers.length}
                    </Typography>
                  </Box>
                  <GroupIcon sx={{ fontSize: 40, color: theme.palette.success.main, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Records Used
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {accountDetails.current_usage_records}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        / {accountDetails.monthly_limit_records === -1 ? '∞' : accountDetails.monthly_limit_records}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getUsagePercentage(accountDetails.current_usage_records, accountDetails.monthly_limit_records)}
                      color={getUsageColor(getUsagePercentage(accountDetails.current_usage_records, accountDetails.monthly_limit_records)) as any}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, color: theme.palette.info.main, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Storage Used
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {Number(accountDetails.current_usage_storage_gb).toFixed(1)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        / {accountDetails.monthly_limit_storage_gb} GB
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getUsagePercentage(Number(accountDetails.current_usage_storage_gb), accountDetails.monthly_limit_storage_gb)}
                      color={getUsageColor(getUsagePercentage(Number(accountDetails.current_usage_storage_gb), accountDetails.monthly_limit_storage_gb)) as any}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <StorageIcon sx={{ fontSize: 40, color: theme.palette.warning.main, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Card elevation={1} sx={{ borderRadius: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ px: 3, pt: 2 }}
          >
            <Tab
              icon={<SettingsIcon />}
              label="Settings"
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 500 }}
            />
            <Tab
              icon={<GroupIcon />}
              label="Users"
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 500 }}
            />
            <Tab
              icon={<IntegrationIcon />}
              label="Integrations"
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 500 }}
            />
          </Tabs>
        </Box>

        <CardContent sx={{ p: 0 }}>
          {/* Settings Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ px: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Account Settings
                </Typography>
                {canManageSettings() && (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setSettingsDialog(true)}
                  >
                    Edit Settings
                  </Button>
                )}
              </Box>

              {accountDetails && (
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">Account Name</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>{accountDetails.name}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary">Plan Type</Typography>
                            <Chip
                              label={accountDetails.plan_type}
                              color={getPlanColor(accountDetails.plan_type) as any}
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary">Plan Status</Typography>
                            <Chip
                              label={accountDetails.plan_status}
                              color={accountDetails.plan_status === 'active' ? 'success' : 'warning'}
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>Usage Limits</Typography>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">Monthly Records</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {accountDetails.monthly_limit_records === -1 ? 'Unlimited' : accountDetails.monthly_limit_records.toLocaleString()}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary">Storage Limit</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {accountDetails.monthly_limit_storage_gb} GB
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary">Email Inboxes</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {accountDetails.monthly_limit_inboxes}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>Billing Information</Typography>
                        <Grid container spacing={3}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="body2" color="text.secondary">Billing Cycle</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {formatDate(accountDetails.billing_cycle_start)} - {formatDate(accountDetails.billing_cycle_end)}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="body2" color="text.secondary">Account Created</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {formatDate(accountDetails.created_at)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>
          </TabPanel>

          {/* Users Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ px: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Account Users
                </Typography>
                {canManageUsers() && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setInviteDialog(true)}
                  >
                    Invite User
                  </Button>
                )}
              </Box>

              {/* User Filters */}
              <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 250 }}
                />

                <TextField
                  select
                  label="Role"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="owner">Owner</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="member">Member</MenuItem>
                </TextField>
              </Box>

              {/* Users Table */}
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell>Status</TableCell>
                      {canManageUsers() && <TableCell align="right">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              src={user.avatar_url}
                              sx={{ width: 32, height: 32 }}
                            >
                              {user.first_name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {user.first_name && user.last_name
                                  ? `${user.first_name} ${user.last_name}`
                                  : user.email.split('@')[0]
                                }
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {user.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.role}
                            size="small"
                            color={getRoleColor(user.role) as any}
                            sx={{ textTransform: 'capitalize' }}
                            icon={user.role === 'owner' ? <ShieldIcon /> : user.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(user.joined_at)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={user.is_active ? 'success' : 'error'}
                          />
                        </TableCell>
                        {canManageUsers() && (
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                setUserMenuAnchor(e.currentTarget);
                                setSelectedUser(user);
                              }}
                              aria-label="User actions"
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          {/* Integrations Tab */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ px: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Email Integrations
                </Typography>
                {canManageIntegrations() && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={loadIntegrations}
                      disabled={integrationsLoading}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleCleanupIntegrations}
                      disabled={saving}
                    >
                      Cleanup Invalid
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<GoogleIcon />}
                      onClick={handleConnectGmail}
                      disabled={saving}
                    >
                      Connect Gmail
                    </Button>
                  </Box>
                )}
              </Box>

              {!canManageIntegrations() ? (
                <Alert severity="info">
                  You need admin privileges to manage email integrations for this account.
                </Alert>
              ) : (
                <Box>
                  {/* Integration Errors */}
                  {integrationsError && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setIntegrationsError('')}>
                      {integrationsError}
                    </Alert>
                  )}

                  {/* Loading State */}
                  {integrationsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : integrations.length === 0 ? (
                    /* Empty State */
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        No Email Integrations
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                        Connect your Gmail account to automatically process freight documents from email attachments.
                        The system will check for unread emails every 5 minutes and process PDF attachments.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<GoogleIcon />}
                        onClick={handleConnectGmail}
                        disabled={saving}
                      >
                        Connect Gmail Account
                      </Button>
                    </Box>
                  ) : (
                    /* Integrations List */
                    <Grid container spacing={3}>
                      {integrations.map((integration) => (
                        <Grid size={{ xs: 12, md: 6 }} key={integration.id}>
                          <Card variant="outlined">
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  {integration.integration_type === 'gmail' ? (
                                    <GoogleIcon color="primary" />
                                  ) : (
                                    <EmailIcon color="primary" />
                                  )}
                                  <Box>
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                      {integration.email_address}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {integration.integration_type === 'gmail' ? 'Gmail' : integration.integration_type}
                                    </Typography>
                                  </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {integration.is_active ? (
                                    <Chip
                                      icon={<CheckCircleIcon />}
                                      label="Active"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                    />
                                  ) : (
                                    <Chip
                                      icon={<WarningIcon />}
                                      label="Expired/Revoked"
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                    />
                                  )}

                                  <IconButton
                                    size="small"
                                    onClick={() => handleDisconnectIntegration(integration.id, integration.email_address)}
                                    disabled={saving || !integration.is_active}
                                    color="error"
                                    title={integration.is_active ? "Disconnect integration" : "Integration is already inactive"}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Box>

                              {!integration.is_active && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                  <Typography variant="body2">
                                    This integration is inactive because the tokens have expired or been revoked.
                                    Use "Cleanup Invalid" to remove it, then reconnect.
                                  </Typography>
                                </Alert>
                              )}

                              <Divider sx={{ my: 2 }} />

                              <Stack spacing={1}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Connected:
                                  </Typography>
                                  <Typography variant="body2">
                                    {formatDate(integration.connected_at)}
                                  </Typography>
                                </Box>

                                {integration.last_sync_at && (
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Last Sync:
                                    </Typography>
                                    <Typography variant="body2">
                                      {formatDate(integration.last_sync_at)}
                                    </Typography>
                                  </Box>
                                )}

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Sync Frequency:
                                  </Typography>
                                  <Typography variant="body2">
                                    Every {integration.sync_frequency_minutes || 5} minutes
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {/* Help Text */}
                  <Alert severity="info" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                      <strong>How it works:</strong> Once connected, our system automatically checks your Gmail every 5 minutes for
                      unread emails with PDF attachments. Freight documents are processed automatically and added to your account.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Troubleshooting:</strong> If an integration shows as "Expired/Revoked", the Gmail tokens have become invalid
                      (usually due to password changes or manual revocation). Use "Cleanup Invalid" to remove expired integrations,
                      then reconnect.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Box>
          </TabPanel>
        </CardContent>
      </Card>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (selectedUser) handleEditUser(selectedUser);
            setUserMenuAnchor(null);
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          Edit Role
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedUser) handleRemoveUser(selectedUser.user_id);
            setUserMenuAnchor(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Remove User
        </MenuItem>
      </Menu>

      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onClose={() => setSettingsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Account Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Account Name"
              value={settingsForm.name}
              onChange={(e) => setSettingsForm({ ...settingsForm, name: e.target.value })}
            />
            <TextField
              fullWidth
              select
              label="Plan Type"
              value={settingsForm.plan_type}
              onChange={(e) => setSettingsForm({ ...settingsForm, plan_type: e.target.value })}
            >
              <MenuItem value="starter">Starter</MenuItem>
              <MenuItem value="essentials">Essentials</MenuItem>
              <MenuItem value="pro">Pro</MenuItem>
              <MenuItem value="enterprise">Enterprise</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialog(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveSettings}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialog} onClose={() => setInviteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite User to Account</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
            />
            <TextField
              fullWidth
              select
              label="Role"
              value={inviteForm.role}
              onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as any })}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="owner">Owner</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialog(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleInviteUser}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {saving ? 'Inviting...' : 'Invite User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog} onClose={() => setEditUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User Role</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              select
              label="Role"
              value={editUserForm.role}
              onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value as any })}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="owner">Owner</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserDialog(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveUserEdit}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gmail Connection Dialog */}
      <Dialog open={gmailDialog} onClose={() => setGmailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <GoogleIcon color="primary" />
            Connect Gmail Account
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert severity="info">
              <Typography variant="body2">
                Enter your Gmail address to check for conflicts before connecting. The same Gmail account cannot be connected to multiple accounts.
              </Typography>
            </Alert>

            <TextField
              fullWidth
              label="Gmail Address"
              type="email"
              value={gmailForm.emailAddress}
              onChange={(e) => setGmailForm({ ...gmailForm, emailAddress: e.target.value })}
              placeholder="example@gmail.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />

            {gmailForm.emailAddress && (
              <Button
                variant="outlined"
                onClick={handleCheckGmailConflict}
                disabled={checkingConflict}
                startIcon={checkingConflict ? <CircularProgress size={16} /> : <SearchIcon />}
              >
                {checkingConflict ? 'Checking...' : 'Check for Conflicts'}
              </Button>
            )}

            {gmailConflictData && (
              <Box>
                {gmailConflictData.hasConflict ? (
                  <Alert severity="error">
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>❌ Cannot Connect:</strong> This Gmail account is already connected to:
                    </Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {gmailConflictData.conflictingAccounts.map((account) => (
                        <li key={account.accountId}>
                          <strong>{account.accountName}</strong>
                        </li>
                      ))}
                    </ul>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      <strong>Solutions:</strong>
                    </Typography>
                    <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Disconnect from the other account first</li>
                      <li>Use a different Gmail account for this account</li>
                      <li>Set up email forwarding to a dedicated account</li>
                    </ol>
                  </Alert>
                ) : (
                  <Alert severity="success">
                    <Typography variant="body2">
                      <strong>✅ No conflicts found!</strong> This Gmail account can be safely connected to this account.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGmailDialog(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleProceedWithGmail}
            variant="contained"
            disabled={!gmailConflictData?.canConnect || saving}
            startIcon={saving ? <CircularProgress size={16} /> : <GoogleIcon />}
          >
            {saving ? 'Connecting...' : 'Connect Gmail'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AccountManager; 